/**
 * Use the Title - Section to introduce secondary content or as a page hero with supporting information that requires more emphasis than the page title by the breadcrumbs, but less than a traditional Hero.
 *
 * @module views/components/TitleSection
 * @memberof -Common
 */
import './TitleSection.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import { constants } from '@ulta/utils/constants/constants';

import ActionGroup from '../ActionGroup/ActionGroup';

/**
 * Represents a TitleSection component
 *
 * @method
 * @param {TitleSectionProps} props - React properties passed from composition
 * @returns TitleSection
 */
export const TitleSection = function( props ){
  const titleSectionRef = useRef( null );
  const { breakpoint } = useDeviceInflection();
  const isMobile = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'SM' );
  const {
    image,
    tag,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    subheadline,
    textSize,
    mobileLeftAlign,
    actionGroup,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props;
  useIntersectionObserver( titleSectionRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  if( !headline || !textSize ){
    return null;
  }

  return (
    <div className={
      classNames( 'TitleSection', {
        ['TitleSection--mobileLeft']:mobileLeftAlign
      } )
    }
    ref={ titleSectionRef }
    >
      <GridContainer>
        { image && (
          <div className='TitleSection__image'>
            <UltaAsset
              { ...spreadProps( { ...image } ) }
            />
          </div>
        ) }
        { tag && (
          <div className='TitleSection__tag'>
            <Tag
              message={ tag }
              style='default'
            />
          </div>
        ) }
        { eyebrow && (
          <div className='TitleSection__eyebrow'>
            <Text
              htmlTag='span'
              textStyle='eyebrow'
              textAlign={ isMobile && mobileLeftAlign ? 'left' : 'center' }
            >
              { eyebrow }
            </Text>
          </div>
        ) }
        { headline && (
          <div className='TitleSection__headline'>
            <Text
              htmlTag={ headlineTag }
              textStyle={ textSize === 'page-title' ? 'title-5' : 'title-4' }
              textAlign={ isMobile && mobileLeftAlign ? 'left' : 'center' }
            >
              { headline }
            </Text>
          </div>
        ) }
        { subheadline && (
          <div className='TitleSection__subheadline'>
            <Text
              htmlTag='p'
              textStyle={ textSize === 'page-title' ? 'subtitle-2' : 'subtitle-1' }
              textAlign={ isMobile && mobileLeftAlign ? 'left' : 'center' }
            >
              { subheadline }
            </Text>
          </div>
        ) }
        { actionGroup?.action1?.label && (
          <div className='TitleSection__action'>
            <ActionGroup
              action1={ actionGroup.action1 }
              action1Style={ actionGroup.action1Style }
              ariaLabel={ headline }
            />
          </div>
        ) }
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef TitleSectionProps
 * @type {object}
 * @property {object} image - Sets the Header image
 * @property {string} tag - Sets the Tag
 * @property {string} eyebrow - Sets the eyebrow text
 * @property {string} headline  - Sets the headline  text
 * @property {string} headlineTag - Sets the headlineTag text
 * @property {string} subheadline - Sets the subheadline
 * @property {string} textSize - Sets the text size for the headline and subheadline
 * @property {boolean} mobileLeftAlign - Sets Text layout options on mobile
 * @property {object} actionGroup - Set the actionGroup
 */
export const propTypes =  {
  image: PropTypes.object,
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  headlineTag: PropTypes.string,
  subheadline: PropTypes.string,
  textSize: PropTypes.string,
  mobileLeftAlign: PropTypes.bool,
  actionGroup: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} example='hello world' - The default refactor or delete
 */
export const defaultProps = {
  headlineTag: 'h2',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

TitleSection.propTypes = propTypes;
TitleSection.defaultProps = defaultProps;

export default TitleSection;
